import { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { propertyService } from 'services/api';

/**
 * Buscador de conjuntos (reemplaza al <select> nativo de propiedad).
 *  - Se escribe para buscar por nombre, NIT o ciudad: la búsqueda la hace el SERVIDOR
 *    (GET /properties/selector), así que no hay tope de 200 conjuntos.
 *  - Teclado: ↑ ↓ Inicio Fin, Enter elige, Esc cierra, Tab sale. Lector de pantalla: patrón combobox/listbox.
 * Props:
 *   value        — id del conjunto elegido ('' = ninguno)
 *   onChange     — fn(id: string, propiedad: object|null)  (la propiedad trae id, name, city, nit y la suscripción)
 *   placeholder  — texto de la opción vacía, p. ej. «— Todas las propiedades —»
 *   allowEmpty   — muestra la opción vacía arriba para limpiar la selección (por defecto sí)
 *   params       — filtros extra para la lista (p. ej. { sin_logistica: 1 })
 */

// Nombre de cada conjunto ya visto, para mostrar el elegido sin volver a pedirlo.
const nombres = new Map();
const POR_PAGINA = 20;

export default function PropertyCombobox({
  value, onChange, placeholder = '— Selecciona una propiedad —', allowEmpty = true,
  disabled = false, minWidth = 220, maxWidth = 320, ariaLabel = 'Propiedad', params,
}) {
  const listId = useId();
  const [abierto, setAbierto]   = useState(false);
  const [texto, setTexto]       = useState('');
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError]       = useState('');
  const [activo, setActivo]     = useState(0);
  const [etiqueta, setEtiqueta] = useState('');
  const [reintento, setReintento] = useState(0);
  const raiz = useRef(null);
  const entrada = useRef(null);
  const boton = useRef(null);
  const lista = useRef(null);
  const panel = useRef(null);
  const [pos, setPos] = useState(null);
  const secuencia = useRef(0);

  // La fila «vacía» (limpiar) solo se ofrece sin texto de búsqueda: al escribir solo se ven coincidencias.
  const conVacia = allowEmpty && !texto.trim();
  const opciones = conVacia ? [{ id: '', name: placeholder, vacio: true }, ...items] : items;
  const idOpcion = (i) => `${listId}-op-${i}`;
  const paramsClave = JSON.stringify(params || {}); // los filtros extra (p. ej. sin_logistica) reinician la búsqueda

  // Nombre del conjunto elegido (puede no estar en la página actual de resultados).
  useEffect(() => {
    if (!value) { setEtiqueta(''); return undefined; }
    const clave = String(value);
    if (nombres.has(clave)) { setEtiqueta(nombres.get(clave)); return undefined; }
    let vivo = true;
    propertyService.selector({ id: clave })
      .then(r => {
        const p = (r.data?.data || [])[0];
        if (p) nombres.set(clave, p.name);
        if (vivo) setEtiqueta(p ? p.name : '');
      })
      .catch(() => { if (vivo) setEtiqueta(''); });
    return () => { vivo = false; };
  }, [value]);

  // Búsqueda en el servidor mientras está abierto (con una pausa corta al escribir).
  useEffect(() => {
    if (!abierto) return undefined;
    const espera = texto ? 250 : 0;
    const t = setTimeout(() => {
      const mia = ++secuencia.current;
      setCargando(true); setError('');
      propertyService.selector({ ...params, search: texto.trim() || undefined, limit: POR_PAGINA })
        .then(r => {
          if (mia !== secuencia.current) return; // llegó una respuesta más vieja que otra ya pedida
          const lista = r.data?.data || [];
          lista.forEach(p => nombres.set(String(p.id), p.name));
          setItems(lista); setTotal(r.data?.total || 0); setCargando(false);
          setActivo(0);
        })
        .catch(e => {
          if (mia !== secuencia.current) return;
          setItems([]); setTotal(0); setCargando(false);
          setError(e.response?.data?.message || 'No se pudo cargar la lista. Intenta de nuevo.');
        });
    }, espera);
    return () => clearTimeout(t);
  }, [abierto, texto, reintento, allowEmpty, paramsClave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al abrir (cuando el panel ya está dibujado): foco en la caja de búsqueda.
  useEffect(() => {
    if (abierto && pos) entrada.current?.focus();
  }, [abierto, pos !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cerrar: se limpia el texto y la posición.
  useEffect(() => {
    if (!abierto) { setTexto(''); setActivo(0); setPos(null); }
  }, [abierto]);

  // El panel se dibuja en <body> (portal) con posición fija junto al botón: así ninguna tarjeta ni
  // contenedor con overflow de la página lo tapa o lo recorta.
  useEffect(() => {
    if (!abierto) return undefined;
    const calcular = () => {
      const r = boton.current?.getBoundingClientRect();
      if (!r) return;
      const ancho = Math.min(Math.max(r.width, 340), window.innerWidth - 16);
      const izquierda = Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8));
      const abajo = window.innerHeight - r.bottom;
      const arriba = r.top;
      const sube = abajo < 390 && arriba > abajo;
      setPos(sube
        ? { bottom: window.innerHeight - r.top + 4, left: izquierda, width: ancho }
        : { top: r.bottom + 4, left: izquierda, width: ancho });
    };
    calcular();
    window.addEventListener('resize', calcular);
    window.addEventListener('scroll', calcular, true);
    return () => { window.removeEventListener('resize', calcular); window.removeEventListener('scroll', calcular, true); };
  }, [abierto]);

  // Clic fuera cierra.
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      const dentro = (raiz.current && raiz.current.contains(e.target)) || (panel.current && panel.current.contains(e.target));
      if (!dentro) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  // La opción activa siempre a la vista, desplazando SOLO la lista (no la página).
  useEffect(() => {
    if (!abierto) return;
    const ul = lista.current;
    const li = document.getElementById(idOpcion(activo));
    if (!ul || !li) return;
    if (li.offsetTop < ul.scrollTop) ul.scrollTop = li.offsetTop;
    else if (li.offsetTop + li.offsetHeight > ul.scrollTop + ul.clientHeight) ul.scrollTop = li.offsetTop + li.offsetHeight - ul.clientHeight;
  }, [activo, abierto, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const elegir = (op) => {
    if (!op) return;
    setAbierto(false);
    boton.current?.focus();
    if (op.vacio) onChange('', null);
    else onChange(String(op.id), op);
  };

  const teclado = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActivo(i => Math.min(i + 1, opciones.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Home') { e.preventDefault(); setActivo(0); }
    else if (e.key === 'End') { e.preventDefault(); setActivo(Math.max(opciones.length - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); elegir(opciones[activo]); }
    else if (e.key === 'Escape') { e.preventDefault(); setAbierto(false); boton.current?.focus(); }
    else if (e.key === 'Tab') setAbierto(false);
  };

  const tecladoBoton = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setAbierto(true); }
  };

  const textoBoton = value ? (etiqueta || 'Cargando…') : placeholder;

  return (
    <div className="pcb" ref={raiz} style={{ minWidth, maxWidth }}>
      <button
        type="button" ref={boton} disabled={disabled}
        className="form-control pcb-trigger"
        aria-haspopup="listbox" aria-expanded={abierto}
        aria-label={`${ariaLabel}: ${value ? textoBoton : 'ninguna seleccionada'}`}
        onClick={() => setAbierto(a => !a)} onKeyDown={tecladoBoton}
      >
        <span className={value ? '' : 'pcb-placeholder'}>{textoBoton}</span>
        <i className="fas fa-chevron-down" aria-hidden="true" />
      </button>

      {abierto && pos && createPortal(
        <div className="pcb-panel" ref={panel} style={pos}>
          <input
            ref={entrada} type="text" className="form-control pcb-search"
            role="combobox" aria-expanded="true" aria-autocomplete="list"
            aria-controls={listId} aria-activedescendant={opciones.length ? idOpcion(activo) : undefined}
            aria-label="Buscar propiedad" placeholder="Buscar por nombre, NIT o ciudad…"
            value={texto} maxLength={100} autoComplete="off"
            onChange={e => setTexto(e.target.value)} onKeyDown={teclado}
          />
          <ul id={listId} ref={lista} role="listbox" className="pcb-list" aria-label="Propiedades">
            {opciones.map((op, i) => (
              <li
                key={op.vacio ? 'vacio' : op.id} id={idOpcion(i)} role="option"
                aria-selected={op.vacio ? !value : String(op.id) === String(value)}
                className={`pcb-opt${i === activo ? ' activo' : ''}${(op.vacio ? !value : String(op.id) === String(value)) ? ' elegido' : ''}${op.vacio ? ' vacio' : ''}`}
                onMouseEnter={() => setActivo(i)}
                onMouseDown={e => e.preventDefault()}
                onClick={() => elegir(op)}
              >
                <span className="pcb-nombre">{op.name}</span>
                {!op.vacio && (op.city || op.nit) && (
                  <span className="pcb-sub">{[op.city, op.nit && `NIT ${op.nit}`].filter(Boolean).join(' · ')}</span>
                )}
              </li>
            ))}
          </ul>
          {cargando && <div className="pcb-msg" role="status">Buscando…</div>}
          {!cargando && error && (
            <div className="pcb-msg pcb-error" role="alert">
              {error} <button type="button" className="pcb-reintentar" onClick={() => setReintento(n => n + 1)}>Reintentar</button>
            </div>
          )}
          {!cargando && !error && items.length === 0 && (
            <div className="pcb-msg" role="status">{texto.trim() ? `Sin resultados para «${texto.trim()}»` : 'No hay propiedades'}</div>
          )}
          {!cargando && !error && total > items.length && (
            <div className="pcb-msg" role="status">Mostrando {items.length} de {total}. Escribe para afinar la búsqueda.</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
